import { BillingGridDatasource } from '@/modules/billing-module/components/grid/config/datasources/billing-grid.datasource'
import { InvoiceStatus, InvoiceResponse } from '@/api/services/billing/common-invoice/billing-common-invoice.dto'
import { CommonFilterModel, CommonParams, ExportCommonParams } from '@/api/resources/common/shared/models/common.models'
import { billingCommonInvoiceService } from '@/api/services/billing/common-invoice/billing-common-invoice.service'
import { AgColumnDefinition, IResponseAgGrid } from '@/shared/models/ag-grid.model'
import { GridUtils } from '@/shared/util/grid-utils'
import { billingGridInvoicesColumnDefs } from '@/modules/billing-module/components/grid/config/column-defs/billing-grid-invoices.column-defs'
import {
    BillingBillableDeliveryNoteSearchGridRequest,
    BillingCaseSearchGridRequest,
    BillingPatientSearchGridRequest
} from '@/api/services/billing/billing.dto'
import {
    BillingSearcherMode,
    BillingSearcherParams,
    BillingSearcherCaseMode
} from '@/modules/billing-module/components/searcher/billing-searcher.model'
import { PAGINATOR_BIG_PAGE_SIZE } from '@/shared/services/services-constants'
import { StrictNullable } from '@/shared/types/app.types'
import { ReportGridRequest } from '@/api/services/common.dto'
import { AxiosResponse } from 'axios'

export class BillingGridInvoicesDatasource extends BillingGridDatasource<InvoiceResponse> {
    private readonly _category: InvoiceStatus

    constructor(laboratoryId: number, mode: InvoiceStatus) {
        super(laboratoryId)
        this._category = mode
    }

    public getColumnDefs(): AgColumnDefinition[] {
        return billingGridInvoicesColumnDefs()
    }
    protected fetchData(filterModel: CommonFilterModel, params: CommonParams): Promise<IResponseAgGrid<InvoiceResponse>> {
        const _filterModel: CommonFilterModel = {
            ...filterModel,
            'status': GridUtils.getSetFilterModel([this._category])
        }
        return billingCommonInvoiceService.paginator(_filterModel, params, this.laboratoryId)
    }

    public exporter(request: ReportGridRequest, params: ExportCommonParams): Promise<AxiosResponse<Blob>> {
        const filterModel: CommonFilterModel = {
            ...request.filterModel,
            'status': GridUtils.getSetFilterModel([this._category])
        }
        return billingCommonInvoiceService.export(this.laboratoryId, { ...request, filterModel }, params)
    }

    public getSearcherModes(): BillingSearcherMode[] {
        return [
            BillingSearcherMode.PATIENT,
            BillingSearcherMode.CASE,
            BillingSearcherMode.DELIVERY_NOTE,
            BillingSearcherMode.CUSTOMER,
            BillingSearcherMode.INVOICE,
            BillingSearcherMode.CASE
        ]
    }
    private async _patientSearcher(patientsIds: number[]): Promise<IResponseAgGrid<InvoiceResponse>> {
        const request: BillingPatientSearchGridRequest = {
            patientsIds
        }
        const params: CommonParams = {
            size: PAGINATOR_BIG_PAGE_SIZE
        }
        return billingCommonInvoiceService.searchPatient(this.laboratoryId, request, params)
    }
    private async _caseSearcher(caseScope: BillingSearcherCaseMode, identifierSearch: string): Promise<StrictNullable<IResponseAgGrid<InvoiceResponse>>> {
        let caseFieldName: BillingCaseSearchGridRequest['caseFieldName'] | undefined
        if (caseScope === BillingSearcherCaseMode.IDENTIFIER) {
            caseFieldName = 'casCasenumber'
        } else if (caseScope === BillingSearcherCaseMode.INSURANCE_DOCUMENT) {
            caseFieldName = 'referralNumber'
        } else if (caseScope === BillingSearcherCaseMode.INSURANCE_POLICY) {
            caseFieldName = 'insuranceNumber'
        }
        if (caseFieldName && identifierSearch) {
            const request: BillingCaseSearchGridRequest = {
                caseFieldName,
                caseFieldValue: identifierSearch
            }
            const params: CommonParams = {
                size: PAGINATOR_BIG_PAGE_SIZE
            }
            return billingCommonInvoiceService.searchCase(this.laboratoryId, request, params)
        }
        return null
    }
    private async _deliveryNoteSearcher(identifierSearch: string): Promise<IResponseAgGrid<InvoiceResponse>> {
        const request: BillingBillableDeliveryNoteSearchGridRequest = {
            billableDeliveryNoteLabel: identifierSearch
        }
        const params: CommonParams = {
            size: PAGINATOR_BIG_PAGE_SIZE
        }
        return billingCommonInvoiceService.searchBillableDeliveryNote(this.laboratoryId, request, params)
    }
    public async searcher(searcherParams: BillingSearcherParams): Promise<StrictNullable<IResponseAgGrid<InvoiceResponse>>> {
        const { mode, caseScope, patientsIds, identifierSearch } = searcherParams
        if (mode === BillingSearcherMode.PATIENT && patientsIds) {
            return this._patientSearcher(patientsIds)
        } else if (mode === BillingSearcherMode.CASE && caseScope && identifierSearch) {
            return this._caseSearcher(caseScope, identifierSearch)
        } else if (mode === BillingSearcherMode.DELIVERY_NOTE && identifierSearch) {
            return this._deliveryNoteSearcher(identifierSearch)
        } else {
            const filterModel: CommonFilterModel = {}
            if (mode === BillingSearcherMode.CUSTOMER) {
                filterModel['customer.name'] = GridUtils.getTextFilterModel('contains', identifierSearch)
            } else if (mode === BillingSearcherMode.INVOICE) {
                filterModel['label'] = GridUtils.getTextFilterModel('contains', identifierSearch)
            }
            const params: CommonParams = {
                size: PAGINATOR_BIG_PAGE_SIZE
            }
            return billingCommonInvoiceService.paginator(filterModel, params, this.laboratoryId)
        }
    }
}
